import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { DsButton, DsSpinner } from "../../../design-system";
import { assetService } from "../../../services/api";
import { SETTINGS_SUB_TABS, fieldLabel, inputSm } from "./constants";

const defaultConfig = {
  numbering: { prefix: "AST", nextNumber: 1, pad: 5 },
  assignmentRules: { requireAck: true, allowSelfRequest: true },
  requestWorkflow: { requireApproval: true, allowReserve: true },
  recoveryRules: { autoOnSeparation: true, sendToFnf: true },
  warrantyAmc: { alertDays: 30 },
  notifications: { emailEnabled: false, systemEnabled: true },
  conditions: [],
  statuses: [],
};

export default function AssetSettingsPanel({
  canEdit,
  sub: subProp,
  onSubChange,
}) {
  const [sub, setSub] = useState(subProp || "categories");
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(defaultConfig);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [draft, setDraft] = useState({ code: "", name: "", description: "", isActive: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (subProp && subProp !== sub) setSub(subProp);
  }, [subProp, sub]);

  const selectSub = (id) => {
    setSub(id);
    onSubChange?.(id);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, catRes, typeRes] = await Promise.all([
        assetService.getConfig().catch(() => null),
        assetService.listCategories().catch(() => ({ data: [] })),
        assetService.listTypes().catch(() => ({ data: [] })),
      ]);
      const cfg = cfgRes?.data?.config || cfgRes?.data || cfgRes?.config || {};
      setConfig({ ...defaultConfig, ...cfg });
      setCategories(Array.isArray(catRes?.data) ? catRes.data : catRes?.data?.categories || []);
      setTypes(Array.isArray(typeRes?.data) ? typeRes.data : typeRes?.data?.types || []);
    } catch {
      /* keep defaults */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveConfig = async () => {
    if (!canEdit) {
      toast.error("View-only access");
      return;
    }
    setSaving(true);
    try {
      await assetService.saveConfig(config);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const createMaster = async (kind) => {
    if (!canEdit) return toast.error("View-only access");
    if (!draft.name.trim()) return toast.error("Name is required");
    try {
      if (kind === "categories") {
        await assetService.createCategory(draft);
      } else {
        await assetService.createType(draft);
      }
      toast.success("Created");
      setDraft({ code: "", name: "", description: "", isActive: true });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Create failed");
    }
  };

  const toggleActive = async (kind, row) => {
    if (!canEdit) return toast.error("View-only access");
    const next = { isActive: !(row.isActive ?? row.is_active) };
    try {
      if (kind === "categories") await assetService.updateCategory(row.id, next);
      else await assetService.updateType(row.id, next);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Update failed");
    }
  };

  const removeMaster = async (kind, id) => {
    if (!canEdit) return toast.error("View-only access");
    if (!window.confirm("Delete this record?")) return;
    try {
      if (kind === "categories") await assetService.deleteCategory(id);
      else await assetService.deleteType(id);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <DsSpinner />
      </div>
    );
  }

  const list = sub === "categories" ? categories : sub === "types" ? types : [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="ds-h1 text-[hsl(var(--ds-fg))]">Asset Settings</h1>
          <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
            Categories, types, numbering, and workflow rules.
          </p>
        </div>
        {canEdit && !["categories", "types"].includes(sub) ? (
          <DsButton type="button" onClick={saveConfig} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </DsButton>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-1 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-2">
        {SETTINGS_SUB_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={
              sub === t.id
                ? "ds-page-nav-item ds-page-nav-item-active !w-auto"
                : "ds-page-nav-item !w-auto"
            }
            onClick={() => selectSub(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {(sub === "categories" || sub === "types") && (
        <div className="space-y-3">
          {canEdit ? (
            <div className="grid grid-cols-1 gap-2 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4 sm:grid-cols-4">
              <div>
                <label className={fieldLabel}>Code</label>
                <input className={inputSm} value={draft.code} onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))} />
              </div>
              <div>
                <label className={fieldLabel}>Name</label>
                <input className={inputSm} value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
              </div>
              <div>
                <label className={fieldLabel}>Description</label>
                <input className={inputSm} value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} />
              </div>
              <div className="flex items-end">
                <DsButton type="button" onClick={() => createMaster(sub)}>Add</DsButton>
              </div>
            </div>
          ) : null}
          <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-left">
                  <th className="px-3 py-2 ds-caption">Code</th>
                  <th className="px-3 py-2 ds-caption">Name</th>
                  <th className="px-3 py-2 ds-caption">Active</th>
                  <th className="px-3 py-2 ds-caption">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(list) ? list : []).map((row) => {
                  const active = row.isActive ?? row.is_active;
                  return (
                    <tr key={row.id} className="border-b border-[hsl(var(--ds-border))]">
                      <td className="px-3 py-2">{row.code}</td>
                      <td className="px-3 py-2">{row.name}</td>
                      <td className="px-3 py-2">{active ? "Yes" : "No"}</td>
                      <td className="px-3 py-2">
                        {canEdit ? (
                          <div className="flex gap-1">
                            <DsButton size="sm" variant="outline" onClick={() => toggleActive(sub, row)}>
                              {active ? "Deactivate" : "Activate"}
                            </DsButton>
                            <DsButton size="sm" variant="danger" onClick={() => removeMaster(sub, row.id)}>
                              Delete
                            </DsButton>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {(Array.isArray(list) ? list : []).length === 0 ? (
              <p className="ds-caption p-4 text-[hsl(var(--ds-muted-fg))]">No records.</p>
            ) : null}
          </div>
        </div>
      )}

      {sub === "numbering" && (
        <div className="max-w-md space-y-2 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4">
          <label className={fieldLabel}>Prefix</label>
          <input
            className={inputSm}
            disabled={!canEdit}
            value={config.numbering?.prefix || ""}
            onChange={(e) =>
              setConfig((c) => ({ ...c, numbering: { ...c.numbering, prefix: e.target.value } }))
            }
          />
          <label className={fieldLabel}>Next number</label>
          <input
            type="number"
            className={inputSm}
            disabled={!canEdit}
            value={config.numbering?.nextNumber ?? 1}
            onChange={(e) =>
              setConfig((c) => ({
                ...c,
                numbering: { ...c.numbering, nextNumber: Number(e.target.value) || 1 },
              }))
            }
          />
          <label className={fieldLabel}>Zero pad</label>
          <input
            type="number"
            className={inputSm}
            disabled={!canEdit}
            value={config.numbering?.pad ?? 5}
            onChange={(e) =>
              setConfig((c) => ({
                ...c,
                numbering: { ...c.numbering, pad: Number(e.target.value) || 5 },
              }))
            }
          />
        </div>
      )}

      {["assignment_rules", "request_workflow", "recovery_rules", "warranty_amc", "notifications", "conditions"].includes(sub) && (
        <div className="max-w-lg space-y-3 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4">
          {sub === "assignment_rules" && (
            <>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={!!config.assignmentRules?.requireAck}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      assignmentRules: { ...c.assignmentRules, requireAck: e.target.checked },
                    }))
                  }
                />
                Require acknowledgement on assign
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={!!config.assignmentRules?.allowSelfRequest}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      assignmentRules: { ...c.assignmentRules, allowSelfRequest: e.target.checked },
                    }))
                  }
                />
                Allow employee self-request
              </label>
            </>
          )}
          {sub === "request_workflow" && (
            <>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={!!config.requestWorkflow?.requireApproval}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      requestWorkflow: { ...c.requestWorkflow, requireApproval: e.target.checked },
                    }))
                  }
                />
                Require approval
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={!!config.requestWorkflow?.allowReserve}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      requestWorkflow: { ...c.requestWorkflow, allowReserve: e.target.checked },
                    }))
                  }
                />
                Allow reserve before assign
              </label>
            </>
          )}
          {sub === "recovery_rules" && (
            <>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={!!config.recoveryRules?.autoOnSeparation}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      recoveryRules: { ...c.recoveryRules, autoOnSeparation: e.target.checked },
                    }))
                  }
                />
                Auto-create recovery on separation
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={!!config.recoveryRules?.sendToFnf}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      recoveryRules: { ...c.recoveryRules, sendToFnf: e.target.checked },
                    }))
                  }
                />
                Allow send to F&F
              </label>
            </>
          )}
          {sub === "warranty_amc" && (
            <div>
              <label className={fieldLabel}>Alert days before expiry</label>
              <input
                type="number"
                className={inputSm}
                disabled={!canEdit}
                value={config.warrantyAmc?.alertDays ?? 30}
                onChange={(e) =>
                  setConfig((c) => ({
                    ...c,
                    warrantyAmc: { ...c.warrantyAmc, alertDays: Number(e.target.value) || 30 },
                  }))
                }
              />
            </div>
          )}
          {sub === "notifications" && (
            <>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={!!config.notifications?.systemEnabled}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      notifications: { ...c.notifications, systemEnabled: e.target.checked },
                    }))
                  }
                />
                System notifications
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  disabled={!canEdit}
                  checked={!!config.notifications?.emailEnabled}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      notifications: { ...c.notifications, emailEnabled: e.target.checked },
                    }))
                  }
                />
                Email notifications
              </label>
            </>
          )}
          {sub === "conditions" && (
            <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
              Status and condition labels use the module defaults (Available, Assigned, Reserved, etc.).
              Custom lists can be stored in config when the API supports them.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
